'use client';

import {
  type FC,
  type MouseEventHandler,
  type PointerEventHandler,
  type WheelEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import MermaidDiagram from '@/components/Diagram/MermaidDiagram';
import type { MermaidDiagram as MermaidDiagramType } from '@/data/blog/types';

interface MermaidFullscreenOverlayProps {
  diagram: MermaidDiagramType | null;
  onClose: () => void;
}

type Vector2D = { x: number; y: number };

interface CanvasTransform {
  scale: number;
  offset: Vector2D;
}

const MIN_ZOOM = 0.24;
const MAX_ZOOM = 3.6;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeDelta = (value: number, mode: number) => (mode === 1 ? value * 16 : value);

export const MermaidFullscreenOverlay: FC<MermaidFullscreenOverlayProps> = ({ diagram, onClose }) => {
  const [mounted, setMounted] = useState(false);
  const target = useMemo(() => (typeof document !== 'undefined' ? document.body : null), []);

  const viewportRef = useRef<HTMLDivElement>(null);
  const pointerStateRef = useRef<{
    id: number;
    origin: Vector2D;
    startOffset: Vector2D;
  } | null>(null);

  const [diagramSize, setDiagramSize] = useState({ width: 0, height: 0 });
  const [hasInitialFit, setHasInitialFit] = useState(false);
  const [userHasAdjusted, setUserHasAdjusted] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  const [transformState, setTransformState] = useState<CanvasTransform>({
    scale: 1,
    offset: { x: 0, y: 0 },
  });

  const transformRef = useRef(transformState);

  const setCanvasTransform = useCallback(
    (updater: CanvasTransform | ((previous: CanvasTransform) => CanvasTransform)) => {
      setTransformState((previous) => {
        const next = typeof updater === 'function' ? (updater as (value: CanvasTransform) => CanvasTransform)(previous) : updater;
        transformRef.current = next;
        return next;
      });
    },
    [],
  );

  // Manage mount lifecycle and body scroll locking
  useEffect(() => {
    if (!diagram) {
      setMounted(false);
      return;
    }

    setMounted(true);

    if (typeof document === 'undefined') return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [diagram]);

  // Reset canvas state whenever the diagram changes
  useEffect(() => {
    if (!diagram) return;

    setDiagramSize({ width: 0, height: 0 });
    setHasInitialFit(false);
    setUserHasAdjusted(false);
    setCanvasTransform({
      scale: 1,
      offset: { x: 0, y: 0 },
    });
  }, [diagram?.code, diagram?.theme, diagram?.title, setCanvasTransform]);

  const fitDiagram = useCallback(
    (mode: 'view' | 'width', options?: { markManual?: boolean }) => {
      const viewport = viewportRef.current;
      if (!viewport || !diagramSize.width || !diagramSize.height) return;

      const rect = viewport.getBoundingClientRect();
      const horizontalPadding = mode === 'width' ? 160 : 220;
      const verticalPadding = mode === 'width' ? 220 : 240;

      const availableWidth = Math.max(rect.width - horizontalPadding, 160);
      const availableHeight = Math.max(rect.height - verticalPadding, 160);

      const widthScale = availableWidth / diagramSize.width;
      const heightScale = availableHeight / diagramSize.height;

      const proposedScale = mode === 'width' ? widthScale : Math.min(widthScale, heightScale);
      const nextScale = clamp(Number.isFinite(proposedScale) && proposedScale > 0 ? proposedScale : 1, MIN_ZOOM, MAX_ZOOM);

      const centeredOffsetX = (rect.width - diagramSize.width * nextScale) / 2;
      const centeredOffsetY = (rect.height - diagramSize.height * nextScale) / 2;

      setCanvasTransform({
        scale: nextScale,
        offset: { x: centeredOffsetX, y: centeredOffsetY },
      });

      setHasInitialFit(true);
      if (options?.markManual) {
        setUserHasAdjusted(true);
      } else {
        setUserHasAdjusted(false);
      }
    },
    [diagramSize, setCanvasTransform],
  );

  // Auto-fit once the diagram dimensions are known
  useEffect(() => {
    if (!diagram || !diagramSize.width || !diagramSize.height || hasInitialFit) return;
    fitDiagram('width', { markManual: false });
  }, [diagram, diagramSize, hasInitialFit, fitDiagram]);

  // Re-fit the diagram on resize if the user hasn't taken control yet
  useEffect(() => {
    if (!diagram || userHasAdjusted) return;

    const handleResize = () => {
      setHasInitialFit(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [diagram, userHasAdjusted]);

  const adjustZoom = useCallback(
    (factor: number, focus?: Vector2D) => {
      setCanvasTransform((previous) => {
        const nextScale = clamp(previous.scale * factor, MIN_ZOOM, MAX_ZOOM);
        if (nextScale === previous.scale) return previous;

        const viewport = viewportRef.current;
        if (!viewport) {
          return {
            scale: nextScale,
            offset: previous.offset,
          };
        }

        const rect = viewport.getBoundingClientRect();
        const focalPoint = focus ?? { x: rect.width / 2, y: rect.height / 2 };

        if (!diagramSize.width || !diagramSize.height) {
          return {
            scale: nextScale,
            offset: previous.offset,
          };
        }

        const contentX = (focalPoint.x - previous.offset.x) / previous.scale;
        const contentY = (focalPoint.y - previous.offset.y) / previous.scale;

        const nextOffsetX = focalPoint.x - contentX * nextScale;
        const nextOffsetY = focalPoint.y - contentY * nextScale;

        return {
          scale: nextScale,
          offset: { x: nextOffsetX, y: nextOffsetY },
        };
      });

      setUserHasAdjusted(true);
    },
    [diagramSize, setCanvasTransform],
  );

  const zoomIn = useCallback(() => adjustZoom(1.18), [adjustZoom]);
  const zoomOut = useCallback(() => adjustZoom(1 / 1.18), [adjustZoom]);

  const handleFitWidth = useCallback(() => fitDiagram('width', { markManual: false }), [fitDiagram]);
  const handleFitView = useCallback(() => fitDiagram('view', { markManual: true }), [fitDiagram]);

  const handleDiagramRender = useCallback(
    ({
      svg,
      container: _container,
    }: {
      svg: SVGSVGElement | null;
      container: HTMLDivElement | null;
    }) => {
      if (!svg) {
        setDiagramSize({ width: 0, height: 0 });
        return;
      }

      let width = 0;
      let height = 0;

      try {
        const box = svg.getBBox();
        width = box.width;
        height = box.height;
      } catch {
        width = svg.viewBox?.baseVal?.width ?? svg.clientWidth ?? svg.getBoundingClientRect().width;
        height = svg.viewBox?.baseVal?.height ?? svg.clientHeight ?? svg.getBoundingClientRect().height;
      }

      if (!width || !height) return;

      setDiagramSize({ width, height });
    },
    [],
  );

  const handleWheel: WheelEventHandler<HTMLDivElement> = (event) => {
    if (!viewportRef.current) return;
    event.preventDefault();

    const deltaX = normalizeDelta(event.deltaX, event.deltaMode);
    const deltaY = normalizeDelta(event.deltaY, event.deltaMode);

    if (event.ctrlKey || event.metaKey || event.altKey) {
      const rect = viewportRef.current.getBoundingClientRect();
      const focalPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const factor = Math.exp(-deltaY / 520);
      adjustZoom(factor, focalPoint);
      return;
    }

    setCanvasTransform((previous) => ({
      scale: previous.scale,
      offset: {
        x: previous.offset.x - deltaX,
        y: previous.offset.y - deltaY,
      },
    }));

    setUserHasAdjusted(true);
  };

  const handlePointerDown: PointerEventHandler<HTMLDivElement> = (event) => {
    if (event.button !== 0) return;
    if (!viewportRef.current) return;

    event.preventDefault();
    viewportRef.current.focus({ preventScroll: true });

    pointerStateRef.current = {
      id: event.pointerId,
      origin: { x: event.clientX, y: event.clientY },
      startOffset: { ...transformRef.current.offset },
    };

    setIsPanning(true);
    setUserHasAdjusted(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = (event) => {
    const pointerState = pointerStateRef.current;
    if (!pointerState || pointerState.id !== event.pointerId) return;

    event.preventDefault();

    const dx = event.clientX - pointerState.origin.x;
    const dy = event.clientY - pointerState.origin.y;

    const nextOffset = {
      x: pointerState.startOffset.x + dx,
      y: pointerState.startOffset.y + dy,
    };

    setCanvasTransform((previous) => ({
      scale: previous.scale,
      offset: nextOffset,
    }));
  };

  const endPan: PointerEventHandler<HTMLDivElement> = (event) => {
    const pointerState = pointerStateRef.current;
    if (!pointerState || pointerState.id !== event.pointerId) return;

    pointerStateRef.current = null;
    setIsPanning(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleDoubleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!viewportRef.current) return;
    event.preventDefault();

    const rect = viewportRef.current.getBoundingClientRect();
    const focalPoint = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    const factor = event.altKey ? 1 / 1.4 : 1.4;
    adjustZoom(factor, focalPoint);
  };

  if (!diagram || !target || !mounted || !diagram.code) {
    return null;
  }

  const zoomLabel = `${Math.round(transformState.scale * 100)}%`;
  const viewportCursor = isPanning ? 'cursor-grabbing' : 'cursor-grab';

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex bg-neutral-950/90 text-white backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={diagram.title ?? 'Mermaid diagram fullscreen'}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(74,76,140,0.28),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(18,32,64,0.32),transparent_52%)]" />

      <div className="relative z-[1] flex h-full w-full flex-col px-4 pb-10 pt-8 sm:px-8 lg:px-12" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 pb-6">
          <div className="max-w-2xl space-y-3 text-left">
            {diagram.title ? (
              <p className="text-xs font-medium uppercase tracking-[0.28em] text-white/70">{diagram.title}</p>
            ) : null}
            {diagram.description ? (
              <p className="text-sm leading-relaxed text-white/70">{diagram.description}</p>
            ) : null}
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/95 text-neutral-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white dark:border-white/20 dark:bg-neutral-900/90 dark:text-white dark:hover:bg-neutral-900"
            type="button"
            aria-label="Close diagram"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="size-5">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="relative flex flex-1">
          <div
            ref={viewportRef}
            tabIndex={-1}
            className={`${viewportCursor} relative flex h-full w-full touch-none select-none items-center justify-center overflow-hidden rounded-[34px] border border-white/10 bg-neutral-950/40 shadow-[0_40px_90px_rgba(10,12,38,0.58)] outline-none backdrop-blur-xl`}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            onPointerLeave={(event) => {
              if (pointerStateRef.current) {
                endPan(event);
              }
            }}
            onDoubleClick={handleDoubleClick}
          >
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_32%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.07),transparent_65%)]" />

            <div className="relative h-full w-full">
              <div
                className="absolute left-0 top-0 will-change-transform"
                style={{
                  transform: `translate3d(${transformState.offset.x}px, ${transformState.offset.y}px, 0) scale(${transformState.scale})`,
                  transformOrigin: 'top left',
                  width: diagramSize.width || undefined,
                  height: diagramSize.height || undefined,
                }}
              >
                <MermaidDiagram
                  className="max-w-none"
                  code={diagram.code}
                  description={diagram.description}
                  padding="none"
                  surface="flat"
                  theme={diagram.theme}
                  title={diagram.title}
                  fitToContainer={false}
                  onRender={handleDiagramRender}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-8 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/15 bg-neutral-950/80 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.26em] text-white/80 shadow-xl backdrop-blur">
            <button
              type="button"
              onClick={zoomOut}
              className="rounded-full bg-white/10 px-3 py-1 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label="Zoom out"
            >
              −
            </button>
            <span className="min-w-[52px] text-center text-white/70">{zoomLabel}</span>
            <button
              type="button"
              onClick={zoomIn}
              className="rounded-full bg-white/10 px-3 py-1 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label="Zoom in"
            >
              +
            </button>
            <span className="mx-1 h-4 w-px bg-white/20" aria-hidden="true" />
            <button
              type="button"
              onClick={handleFitWidth}
              className="rounded-full bg-white/8 px-3 py-1 text-white/80 transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Fit Width
            </button>
            <button
              type="button"
              onClick={handleFitView}
              className="rounded-full bg-white/8 px-3 py-1 text-white/80 transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Fit View
            </button>
          </div>
        </div>
      </div>
    </div>,
    target,
  );
};

export default MermaidFullscreenOverlay;
